// Local-mode fixtures for the SSH Proxy feature (jump servers, session
// recordings, SSH policy). Development only — installed by ../index.js when
// REACT_APP_LOCAL_MODE is "true", never bundled behaviour in a real env.
//
// The point of these is to make the UI states reachable without a proxy, a jump
// box or a backend. Every state the screens can show is represented by a row:
// live / stale / never-seen proxies, an attributed and an unattributed
// recording, a legacy row with the BE-1 fields missing, an intact recording and
// an altered one.

const iso = (msAgo) => new Date(Date.now() - msAgo).toISOString();

const CA_KEY =
  "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIPk2mQ4rV8sN1wXcZ0tB7hLfJ9yR3oKdEu6nGa2iTqWs authnull-sshproxy-ca";
const BACKEND_KEY =
  "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIB8f2Q1rXwE6vKp0mZ9cYyR4oK8wEf6nLg2hJdA3sTuV authnull-sshproxy-backend";
const HOST_FP = "SHA256:7Hn2vK8sQ1dXwR4oP9zLmB3cYyE6nJfA0tGu5iTqWsM";

// Which row the wizard adopts after Step 1. Point this at 2 or 3 to exercise
// the stale / never-seen gates on Step 2 instead of the happy path.
export const MOCK_NEW_JUMP_SERVER_ID = 1;

export const MOCK_JUMP_SERVERS = [
  {
    // Live, fully reported — unlocks every Step 2 panel.
    id: 1,
    serverName: "prod-bastion",
    publicIpAddress: "203.0.113.10",
    privateIpAddress: "10.2.14.7",
    connectBy: "0",
    defaults: true,
    status: "active",
    region: "US West",
    lastSeenAt: iso(15 * 1000),
    lastSeenIp: "203.0.113.10",
    proxyVersion: "1.4.2+a1b2c3d",
    proxyListen: ":2222",
    hostKeyFingerprint: HOST_FP,
    backendPublicKey: BACKEND_KEY,
    caPublicKey: CA_KEY,
  },
  {
    // Heartbeat stopped four hours ago → "Last seen 4h ago", amber.
    // caPublicKey null: an ordinary state on a proxy with no CA configured,
    // and what Step 2 renders as "waiting for the proxy to report".
    id: 2,
    serverName: "eu-bastion",
    publicIpAddress: "203.0.113.44",
    privateIpAddress: "10.9.1.4",
    connectBy: "0",
    defaults: false,
    status: "active",
    region: "EU Central",
    lastSeenAt: iso(4 * 60 * 60 * 1000),
    lastSeenIp: "203.0.113.44",
    proxyVersion: "1.3.9+77fe0b1",
    proxyListen: ":2222",
    hostKeyFingerprint: "SHA256:Qd4mZ8xR0vLpT2sK6wYnB9cJfE3oGu1iHa5tXqWsM7N",
    backendPublicKey: BACKEND_KEY,
    caPublicKey: null,
  },
  {
    // Never checked in. NOT "offline" — usually SSHPROXY_CONTROL_URL unset,
    // which is a working state for SSH itself.
    id: 3,
    serverName: "lab-jump",
    publicIpAddress: "198.51.100.23",
    privateIpAddress: "10.44.0.9",
    connectBy: "1",
    defaults: false,
    status: "In progress",
    region: "US East",
    lastSeenAt: null,
    lastSeenIp: null,
    proxyVersion: null,
    proxyListen: null,
    hostKeyFingerprint: null,
    backendPublicKey: null,
    caPublicKey: null,
  },
];

// ─── A real asciicast v2 session ──────────────────────────────────────────────
// Built as data and serialised, so the escapes stay readable. Includes the two
// things that look like bugs and are not: markers recording withheld input, and
// `i` events that duplicate the echoed `o` events.
const CAST_HEADER = {
  version: 2,
  width: 96,
  height: 26,
  timestamp: 1790223816,
  title: "ubuntu@10.0.0.5:22",
  session_id: "d7bbae886dcfe00d",
  principal: "aditya@authnull.com",
  key_fingerprint: "SHA256:1SUiSPHt7uJ0pV3xZ0mQ9c2bYyR4oK8wEf6nLg2hJdA",
  target_user: "ubuntu",
  target_host: "10.0.0.5",
  source_ip: "10.2.14.7",
};

const PROMPT = "\u001b[1;32mubuntu@web-01\u001b[0m:\u001b[1;34m~\u001b[0m$ ";

const CAST_EVENTS = [
  [0.171438, "m", "shell: /bin/bash"],
  [0.173458, "o", "Welcome to Ubuntu 22.04.5 LTS (GNU/Linux 5.15.0-118-generic x86_64)\r\n\r\n"],
  [0.310000, "o", "  System load:  0.08              Processes:             142\r\n"],
  [0.360000, "o", "  Usage of /:   41.2% of 48.28GB   Users logged in:       1\r\n\r\n"],
  [0.480000, "o", "Last login: Tue Sep 23 11:02:41 2026 from 10.2.14.7\r\n"],
  [0.620000, "o", PROMPT],
  [1.760429, "i", "w"],
  [1.761850, "o", "w"],
  [1.900000, "i", "h"],
  [1.901200, "o", "h"],
  [2.010000, "i", "o"],
  [2.011400, "o", "o"],
  [2.120000, "i", "a"],
  [2.121600, "o", "a"],
  [2.230000, "i", "m"],
  [2.231800, "o", "m"],
  [2.340000, "i", "i"],
  [2.341000, "o", "i"],
  [2.450000, "i", "\r"],
  [2.451000, "o", "\r\n"],
  [2.520000, "o", "ubuntu\r\n"],
  [2.560000, "o", PROMPT],
  [4.100000, "o", "sudo systemctl restart nginx\r\n"],
  [4.260000, "o", "[sudo] password for ubuntu: "],
  // The proxy withholds typed bytes it never saw echoed back. A password at a
  // sudo prompt is exactly that: the remote side turns echo off and no packet
  // announces it. The marker is what keeps the gap explained.
  [4.270000, "m", "input withheld: no echo from remote (6 bytes suppressed)"],
  [6.980000, "o", "\r\n"],
  [7.640000, "o", PROMPT],
  [9.200000, "o", "systemctl is-active nginx\r\n"],
  [9.410000, "o", "\u001b[32mactive\u001b[0m\r\n"],
  [9.460000, "o", PROMPT],
  [11.30000, "o", "tail -n 3 /var/log/nginx/access.log\r\n"],
  [11.52000, "o", '10.2.14.7 - - [23/Sep/2026:11:04:02 +0000] "GET /healthz HTTP/1.1" 200 2\r\n'],
  [11.53000, "o", '10.2.14.7 - - [23/Sep/2026:11:04:12 +0000] "GET /healthz HTTP/1.1" 200 2\r\n'],
  [11.54000, "o", '10.2.14.7 - - [23/Sep/2026:11:04:22 +0000] "GET /healthz HTTP/1.1" 200 2\r\n'],
  [11.60000, "o", PROMPT],
  [13.10000, "o", "exit\r\n"],
  [13.20000, "m", "session closed by client"],
  [13.25000, "o", "logout\r\n"],
];

const CAST_TEXT = [JSON.stringify(CAST_HEADER), ...CAST_EVENTS.map((e) => JSON.stringify(e))].join("\n");

// UTF-8 safe base64 — the transport is base64 precisely because a terminal
// recording carries control bytes that do not survive a JSON string.
function toBase64(text) {
  const bytes = new TextEncoder().encode(text);
  let binary = "";
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

export const MOCK_CAST_BASE64 = toBase64(CAST_TEXT);
// The same session recorded from a TALL terminal window (56 rows): what a real
// operator's window often looks like. The player should crop the rows the
// session never reached instead of showing a narrow column of black.
const TALL_CAST_BASE64 = toBase64(
  [JSON.stringify({ ...CAST_HEADER, height: 56 }), ...CAST_EVENTS.map((e) => JSON.stringify(e))].join("\n"),
);

export const MOCK_SESSION_RECORDINGS = [
  {
    // Fully attributed SSH proxy session.
    id: 9001,
    sessionId: "d7bbae886dcfe00d",
    jumpServerId: 1,
    userId: 4,
    userName: "aditya@authnull.com",
    emailAddress: "aditya@authnull.com",
    endpoint: "web-01.prod",
    targetHost: "10.0.0.5",
    targetAccount: "ubuntu",
    identityMethod: "ssh-key",
    presenceMethod: "push",
    sizeBytes: 18432,
    recordingMimeType: "asciicast",
    recordingUrl: "",
    sessionRecordingTime: iso(6 * 60 * 1000),
  },
  {
    // userId null: no registered key matched the fingerprint that authenticated
    // the session, so the operator column reads "Unattributed" rather than
    // putting a guessed name in an audit trail.
    id: 9002,
    sessionId: "3f91cc20ab7e4d18",
    jumpServerId: 1,
    userId: null,
    userName: "oracle",
    emailAddress: "",
    endpoint: "db-01.prod",
    targetHost: "db-01.prod",
    targetAccount: "oracle",
    identityMethod: "ssh-key",
    presenceMethod: "push",
    sizeBytes: 4096,
    recordingMimeType: "asciicast",
    recordingUrl: "",
    sessionRecordingTime: iso(52 * 60 * 1000),
  },
  {
    // A Guacamole-era row. It predates every BE-1 field, so it omits them —
    // which is what the em dashes in the grid are for.
    id: 9003,
    userId: 2,
    userName: "saffran@authnull.com",
    emailAddress: "saffran@authnull.com",
    endpoint: "web-02.stage",
    recordingMimeType: "typescript",
    recordingUrl: "",
    sessionRecordingTime: iso(3 * 60 * 60 * 1000),
  },
  {
    // Screen (Guacamole) rows carry a presigned recordingUrl and a length
    // instead of bytes behind getRecordingContent.
    id: 9004,
    userId: 1,
    userName: "ubuntu",
    emailAddress: "guptasatyam1807@gmail.com",
    endpoint: "web-01.prod",
    recordingMimeType: "guac",
    recordingLength: "04:12",
    recordingUrl: "about:blank",
    sessionRecordingTime: iso(26 * 60 * 1000),
  },
  {
    // No length: nothing was captured, so there is nothing to play.
    id: 9005,
    userId: 3,
    userName: "root",
    emailAddress: "sumit@authnull.com",
    endpoint: "db-01.prod",
    recordingMimeType: "guac",
    recordingLength: "",
    recordingUrl: "",
    sessionRecordingTime: iso(4 * 60 * 60 * 1000),
  },
];

// The listing filters on a format token, so the mock does too — otherwise the
// Terminal and Screen tabs both show every row and the format control looks
// broken.
export function mockSessionRecordings(body) {
  const want = body?.filter?.recordingMimeType;
  const rows = want
    ? MOCK_SESSION_RECORDINGS.filter((r) => r.recordingMimeType === want)
    : MOCK_SESSION_RECORDINGS;
  return { code: 200, status: "success", sessionRecordings: rows, total: rows.length };
}

// Keyed by recording id so the viewer can show an intact recording and an
// altered one. 9003 has no ingest hash at all → "unknown".
const MOCK_RECORDING_CONTENT = {
  9001: { integrity: "intact", hmac: "sha256:2f8c41ab9d07e6b35c1a4f90de77b2c8ea53016d4b9f8a27c30e5d61bb74af92" },
  9002: { integrity: "altered", hmac: "sha256:be1049fd3c72a86e5011b7d4fa39c28075e6b1a3d90c47f82be5301aa6cd7f14" },
  9003: { integrity: "unknown", hmac: "" },
};

export function mockRecordingContent(id) {
  const meta = MOCK_RECORDING_CONTENT[id] || MOCK_RECORDING_CONTENT[9001];
  return {
    code: 200,
    status: "success",
    id,
    sessionId: "d7bbae886dcfe00d",
    recordingMimeType: "asciicast",
    sizeBytes: MOCK_CAST_BASE64.length,
    content: Number(id) === 9003 ? TALL_CAST_BASE64 : MOCK_CAST_BASE64,
    integrity: meta.integrity,
    hmac: meta.hmac,
  };
}

// Swap this in as listPolicy's `data` to see the list render. Left empty by
// default because the empty state carries the message that matters: no policy
// means ALLOW, and an empty list is the easiest thing on the screen to misread.
export const MOCK_SSH_POLICY_ROWS = [
  { policyId: 4421, policyName: "ssh-prod-bastion-ubuntu", status: "Approved", credentialType: "SSH" },
];

// Two SSH policies on different gateways and one AD policy. listPolicy is a
// static mock that ignores filters, so the AD row checks the client-side
// gateway filter drops anything without ssh.jumpServers.
export const MOCK_SSH_POLICIES = [
  {
    id: "7f1c2d9e-0000-4000-8000-000000000001",
    policyName: "web-01 ubuntu — ops",
    policyType: "SSH",
    status: "Approved",
    createdAt: iso(3 * 24 * 60 * 60 * 1000),
    updatedAt: iso(2 * 60 * 60 * 1000),
    policyJson: {
      policyType: "SSH",
      policyFlow: "mfa_required",
      ssh: {
        users: ["saffran@authnull.com"], groups: [], matchAll: false, hosts: ["10.2.14.21"], accounts: ["ubuntu"], jumpServers: [1],
        // Two named commands, as NormaliseSSHSudo stores them.
        sudo: { full: false, commands: ["/usr/bin/systemctl restart nginx", "/usr/bin/journalctl"], maxMinutes: 60 },
      },
    },
  },
  {
    id: "7f1c2d9e-0000-4000-8000-000000000002",
    policyName: "eu db oracle — everyone",
    policyType: "SSH",
    status: "Approved",
    createdAt: iso(5 * 24 * 60 * 60 * 1000),
    updatedAt: iso(5 * 24 * 60 * 60 * 1000),
    policyJson: {
      policyType: "SSH",
      policyFlow: "",
      ssh: { users: [], groups: [], matchAll: true, hosts: ["10.9.0.12:2200"], accounts: ["oracle"], jumpServers: [2] },
    },
  },
  {
    // Suspended via revokePolicy: the backend stores status "Suspended"
    // (RevokePolicyJSON), so this row's menu offers Resume instead of Suspend.
    id: "7f1c2d9e-0000-4000-8000-000000000003",
    policyName: "web-01 root — break-glass",
    policyType: "SSH",
    status: "Suspended",
    createdAt: iso(7 * 24 * 60 * 60 * 1000),
    updatedAt: iso(20 * 60 * 60 * 1000),
    policyJson: {
      policyType: "SSH",
      policyFlow: "mfa_required",
      ssh: {
        users: ["aditya@authnull.com"], groups: [], matchAll: false, hosts: ["10.2.14.21"], accounts: ["root"], jumpServers: [1],
        sudo: { full: true, commands: [], maxMinutes: 30 },
      },
    },
  },
  {
    id: "7f1c2d9e-0000-4000-8000-0000000000ad",
    policyName: "AD admins — should never show here",
    policyType: "ad",
    status: "Approved",
    createdAt: iso(9 * 24 * 60 * 60 * 1000),
    updatedAt: iso(9 * 24 * 60 * 60 * 1000),
    policyJson: { policyType: "ad", policyFlow: "mfa_required", ssh: { users: [], groups: [], matchAll: false, hosts: [], accounts: [], jumpServers: [] } },
  },
];

// ssh.sudo stored the way NormaliseSSHSudo leaves it: commands trimmed and
// de-duplicated, maxMinutes defaulted to 60, a block that gives nothing
// dropped. (Validation errors are a 400 on the real backend; the mock adapter
// only answers 200, so the form's client-side checks are what show here.)
const normaliseMockSudo = (policyJson) => {
  const ssh = policyJson?.ssh;
  if (!ssh?.sudo) return policyJson;
  const { full, commands, maxMinutes } = ssh.sudo;
  const cmds = full ? [] : [...new Set((commands || []).map((c) => String(c).trim()).filter(Boolean))];
  const rest = { ...ssh };
  delete rest.sudo;
  if (!full && !cmds.length) return { ...policyJson, ssh: rest };
  return { ...policyJson, ssh: { ...rest, sudo: { full: !!full, commands: cmds, maxMinutes: Number(maxMinutes) || 60 } } };
};

let mockPolicySeq = 100;

// Create and update keep what they were sent in MOCK_SSH_POLICIES, so the list
// (the static "/policy/json/listpolicy" entry below) returns it, ssh.sudo
// included. Matched ahead of SSH_PROXY_MOCKS by findMockResponse.
export const SSH_POLICY_DYNAMIC_MOCKS = [
  {
    pattern: "/policy/json/createpolicy",
    respond: (body) => {
      const id = `7f1c2d9e-0000-4000-8000-${String(++mockPolicySeq).padStart(12, "0")}`;
      const now = new Date().toISOString();
      MOCK_SSH_POLICIES.unshift({
        id,
        policyName: body?.policyName || body?.policyJson?.policyName || "",
        policyType: body?.policyType || "SSH",
        status: "Approved",
        createdAt: now,
        updatedAt: now,
        policyJson: normaliseMockSudo(body?.policyJson || {}),
      });
      return { code: 200, status: "success", data: id };
    },
  },
  {
    pattern: "/policy/json/updatepolicy",
    respond: (body) => {
      const row = MOCK_SSH_POLICIES.find((r) => r.id === body?.id);
      if (row) {
        Object.assign(row, {
          policyName: body?.policyName ?? row.policyName,
          policyJson: normaliseMockSudo(body?.policyJson || row.policyJson),
          updatedAt: new Date().toISOString(),
        });
      }
      return { updatedPolicyID: row ? row.id : "7f1c2d9e-0000-4000-8000-000000000099" };
    },
  },
];

export const MOCK_SSH_KEYS = [
  {
    keyId: 11,
    userId: 4,
    userName: "aditya@authnull.com",
    label: "macbook",
    fingerprint: "SHA256:1SUiSPHt7uJ0pV3xZ0mQ9c2bYyR4oK8wEf6nLg2hJdA",
    publicKey: "ssh-ed25519 AAAAC3NzaC1lZDI1NTE5AAAAIL9xW2vK0sQ1dXwR4oP9zLmB3cYyE6nJfA0tGu5iTqWs aditya@macbook",
    createdAt: iso(9 * 24 * 60 * 60 * 1000),
  },
];

export const MOCK_AD_DOMAINS = [
  { id: 1, directoryName: "test01", domainName: "test01.local", enforcementMode: "enforce" },
];

export const MOCK_OS_LIST = [
  { id: 1, osName: "Windows" },
  { id: 2, osName: "Linux" },
];

const unixSeconds = (msAgo) => Math.floor((Date.now() - msAgo) / 1000);

export const MOCK_INSTANCES = [
  {
    instanceId: 1,
    hostName: "web-01.prod",
    description: "Public web tier, behind the ALB",
    osName: "2",
    osDetail: "Ubuntu 24.04.1 LTS",
    publicIp: "10.0.0.5",
    privateIp: "10.2.14.21",
    jumpServerId: 1,
    fqdn: "web-01.prod.internal",
    status: "Active",
    lastActive: unixSeconds(3 * 60 * 1000),
    countOfUsers: 3,
    countOfGroups: 1,
  },
  {
    instanceId: 2,
    hostName: "db-01.prod",
    description: "Primary Postgres",
    osName: "2",
    publicIp: "10.0.0.9",
    privateIp: "10.2.14.30",
    fqdn: "db-01.prod.internal",
    status: "Active",
    lastActive: unixSeconds(47 * 60 * 1000),
    countOfUsers: 2,
    countOfGroups: 1,
  },
  {
    instanceId: 3,
    hostName: "win-app-02",
    description: "",
    osName: "1",
    publicIp: "",
    privateIp: "10.2.15.8",
    fqdn: "win-app-02.corp.internal",
    status: "Inactive",
    lastActive: unixSeconds(5 * 24 * 60 * 60 * 1000),
    countOfUsers: 0,
    countOfGroups: 0,
  },
  // Decommissioned (cleaned up by its gateway) and still decommissioning.
  {
    instanceId: 4,
    hostName: "old-app-01",
    description: "Retired app server",
    osName: "2",
    publicIp: "",
    privateIp: "10.2.1.40",
    jumpServerId: 1,
    fqdn: "",
    status: "Decommissioned",
    lastActive: unixSeconds(9 * 24 * 60 * 60 * 1000),
    countOfUsers: 0,
    countOfGroups: 0,
  },
  {
    instanceId: 5,
    hostName: "web-02.stage",
    description: "",
    osName: "2",
    publicIp: "",
    privateIp: "10.2.1.14",
    jumpServerId: 1,
    fqdn: "",
    status: "Decommissioning",
    lastActive: unixSeconds(2 * 60 * 60 * 1000),
    countOfUsers: 0,
    countOfGroups: 0,
  },
];

// What the signed-in operator can open (POST /ssh/myTargets): several accounts
// on web-01.prod (1), one on db-01.prod (2). `host` is the address the gateway
// dials and `command` is built the way sshgrants.ConnectCommand builds it:
// "ssh [-p port] account@host@gatewayAddress".
const myTarget = (endpointId, hostname, host, account, extra = {}) => ({
  account,
  host,
  hostname,
  endpointId,
  gatewayId: 1,
  gateway: "prod-bastion",
  gatewayAddress: "203.0.113.10",
  port: "2222",
  policy: "web-01 ops",
  policyId: "7f1c2d9e-0000-4000-8000-000000000001",
  approval: "mfa_required",
  credential: "key",
  command: `ssh -p 2222 ${account}@${host}@203.0.113.10`,
  ...extra,
});
export const MOCK_MY_TARGETS = [
  myTarget(1, "web-01.prod", "10.2.14.21", "alice"),
  myTarget(1, "web-01.prod", "10.2.14.21", "deploy", { credential: "password" }),
  myTarget(1, "web-01.prod", "10.2.14.21", "ubuntu"),
  myTarget(2, "db-01.prod", "10.2.14.30", "oracle", { policy: "db-01 oracle — dba", policyId: "7f1c2d9e-0000-4000-8000-000000000002", approval: "allow" }),
];

// URL substring → response. Merged ahead of the generic table in mockData.js,
// because the generic keys "/policy" and "/endpoint" would otherwise shadow
// "/policy/json/listpolicy" and "/endpointusers/listallepmusers" (first match
// wins, in insertion order).
export const SSH_PROXY_MOCKS = {
  "/jumpserver/listjumpserver": { code: 200, status: "success", jumpServers: MOCK_JUMP_SERVERS, total: MOCK_JUMP_SERVERS.length },
  "/jumpserver/addjumpserver": { code: 200, status: "success", id: MOCK_NEW_JUMP_SERVER_ID },
  "/jumpserver/deletejumpserver": { code: 200, status: "success" },
  "/jumpserver/updatestatus": { status: "success", message: "Gateway status updated" },
  "/sshkeys/listkeys": { code: 200, status: "success", keys: MOCK_SSH_KEYS },
  "/sshkeys/addkey": { code: 200, status: "success", keyId: 12 },
  "/sshkeys/revokekey": { code: 200, status: "success" },
  "/policy/json/createpolicy": { code: 200, status: "success", data: "7f1c2d9e-0000-4000-8000-000000000001" },
  "/policy/json/listpolicy": { code: 200, status: "success", data: MOCK_SSH_POLICIES },
  "/policy/json/updatepolicy": { updatedPolicyID: "7f1c2d9e-0000-4000-8000-000000000099" },
  "/policy/json/deletepolicy": { code: 200, status: "success", message: "Policy deleted" },
  "/policy/json/revokepolicy": { code: 200, status: "success", message: "Policy suspended" },
  "/policy/json/policyaction": { code: 200, status: "success", message: "Policy updated" },
  // Server-rendered connect commands. ssh splits on the last @, so the console
  // never builds these itself.
  "/ssh/listconnections": {
    code: 200,
    status: "success",
    userId: 2,
    email: "saffran@authnull.com",
    connections: [
      {
        host: "10.2.14.21", account: "ubuntu", jumpServerId: 1, jumpServer: "prod-bastion", proxyHost: "203.0.113.10", port: 2222,
        command: "ssh ubuntu@10.2.14.21@203.0.113.10 -p 2222", live: true,
        policyId: "7f1c2d9e-0000-4000-8000-000000000001", policyName: "web-01 ubuntu — ops",
      },
    ],
  },
  "/ssh/mytargets": { principal: "localdev@authnull.com", targets: MOCK_MY_TARGETS },
  "/policy/json/searchpolicy": {
    code: 200,
    status: "success",
    matched: false,
    decision: "allow (no policy)",
    // The near miss the engine names. It is the whole difference between
    // "my policy did not fire" and "I have no policy".
    matchReason: "known user, but not that target account",
  },
  "/ad/getalldomains": { code: 200, status: "success", domains: MOCK_AD_DOMAINS, totalCount: MOCK_AD_DOMAINS.length },
  "/endpointusers/listallepmusers": {
    code: 200,
    status: "success",
    epmUsers: [
      // `instances` is read as row.instances[0] by the endpoint's users tab;
      // without it the endpoint detail page crashes in local mode.
      { userId: 1, userName: "ubuntu", source: "gateway", instances: [{ instanceId: 1, hostname: "web-01.prod", os: "linux" }] },
      { userId: 2, userName: "oracle", source: "gateway", instances: [{ instanceId: 2, hostname: "db-01.prod", os: "linux" }] },
      { userId: 3, userName: "deploy", source: "manual", instances: [{ instanceId: 1, hostname: "web-01.prod", os: "linux" }] },
      // Accounts the password-rotation mocks rotate on web-01.prod, so
      // Endpoint Users' Credential / Rotation columns have something to join.
      ...["alice", "bob", "carol", "sshtest", "svc-backup", "root", "contractor-demo"].map((userName, i) => ({
        userId: 10 + i,
        userName,
        source: "gateway",
        instances: [{ instanceId: 1, hostname: "web-01.prod", os: "linux" }],
      })),
    ],
  },
  // Shared with the Endpoints grid, which is far fussier than the recordings
  // filter that needed it: it calls .toLowerCase() on `status` and formats
  // `lastActive` as a unix timestamp, so a partial row crashes the page rather
  // than rendering blanks. Keep every field below populated.
  //
  // `osName` holds the OS *id* as a string — the grid resolves it against
  // getOsList by `os.id.toString()`, not by name.
  "/instances/list": {
    code: 200,
    status: "success",
    instances: MOCK_INSTANCES,
    totalCount: MOCK_INSTANCES.length,
    total: MOCK_INSTANCES.length,
  },
  // Agentless onboarding. The real handler returns the id as a string under
  // "InstanceId" (capital I).
  "/instances/addinstance": { InstanceId: "901", Code: "201", Message: "Success", Factors: [] },
  "/getoslist": { code: 200, status: "success", osList: MOCK_OS_LIST },
  "/getcountofepmuserbyendpoint": { code: 200, count: 3 },
  "/getcountofepmusergroupbyendpoint": { code: 200, count: 1 },
};
